import { useMsal } from "@azure/msal-react";
import {
  Bell01,
  BellRinging01,
  BookClosed,
  BookOpen01,
  Calendar,
  CalendarCheck01,
  CheckSquareBroken,
  ChevronDown,
  ChevronUp,
  Clock,
  CurrencyDollarCircle,
  Eye,
  File02,
  File06,
  FolderCode,
  GraduationHat02,
  Lightbulb05,
  Menu01,
  PieChart03,
  Settings01,
  Shield01,
  ShieldTick,
  Sliders04,
  Star01,
  Stars03,
  Target04,
  TrendUp01,
  XClose,
  Globe01,
  BarChart10,
  LineChartUp03,
} from "@untitledui/icons";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import MastercardLogo from "@/assets/images/MastercardLogo.svg";
import LanguageSwitcher from "@/Components/LanguageSwitcher";
import TimezoneBar from "@/Components/TimezoneBar";
import TourGuide from "@/Components/TourGuide";
import { useAuth } from "@/hooks/useAuth";
import { useDeviceType } from "@/hooks/useDeviceType";
import TooltipButton from "@/shared/components/TooltipButton";
import { ROLE_NAME_MAP } from "@/shared/utils/maps";
import {
  selectLanguageSwitchIcon,
  selectNotificationIcon,
  selectTourIcon,
} from "@/store/adminSlice";
import { useAppSelector } from "@/store/hooks";
import { selectRoles, selectSelectedRole, setSelectedRole } from "@/store/userSlice";
import {
  fetchUserDetailsByEmail,
  resetRoleState,
  selectFeatures,
  selectFeaturesLoading,
  selectFeaturesStatus,
} from "@/store/userSlice";

import NotificationsDropdown from "../Notifications/component/NotificationDropDown";
import ProfileMenu from "./components/ProfileMenu";
import type { NavbarProps } from "./types/navbar";

const cn = (...classes: (string | false | null | undefined)[]) => classes.filter(Boolean).join(" ");

const Navbar: React.FC<NavbarProps> = ({ drawerOpen, setDrawerOpen }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { isTablet } = useDeviceType();
  const features = useAppSelector(selectFeatures);
  const featuresLoading = useAppSelector(selectFeaturesLoading);
  const featureStatus = useAppSelector(selectFeaturesStatus);
  const showTourIcon = useAppSelector(selectTourIcon);
  const showNotificationIcon = useAppSelector(selectNotificationIcon);

  const showLanguageSwitch = useAppSelector(selectLanguageSwitchIcon);
  const [tourOpen, setTourOpen] = useState(false);

  const dispatch = useDispatch();
  const selectedRole = useSelector(selectSelectedRole);

  const { accounts } = useMsal();
  const { account, logout } = useAuth();
  const [languageOpen, setLanguageOpen] = useState(false);

  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement | null>(null);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const firstSidebarItemRef = useRef<HTMLButtonElement | null>(null);
  const menuBtnRef = useRef<HTMLButtonElement | null>(null); // ✅ ADD

  const [tourTrigger, setTourTrigger] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const roles = useSelector(selectRoles);
  const { isMobile } = useDeviceType();

  const getColor = (key: string) => {
    const colors = [
      "bg-red-100 text-red-600",
      "bg-blue-100 text-blue-600",
      "bg-green-100 text-green-600",
      "bg-yellow-100 text-yellow-600",
      "bg-purple-100 text-purple-600",
      "bg-pink-100 text-pink-600",
      "bg-indigo-100 text-indigo-600",
    ];

    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = key.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  useEffect(() => {
    if (drawerOpen) {
      firstSidebarItemRef.current?.focus();
    }
  }, [drawerOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(target) &&
        (!menuBtnRef.current || !menuBtnRef.current.contains(target))
      ) {
        setDrawerOpen(false);
      }

      if (notifRef.current && notifOpen && !notifRef.current.contains(target)) {
        setNotifOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setDrawerOpen, notifOpen]);

  const displayName =
    accounts[0]?.name || account?.displayName || t("components.navBar.header.guest");

  const userPrincipalName = accounts[0]?.username || account?.userPrincipalName || "";

  const widgetIconMap: Record<string, React.ReactNode> = {
    "dashboard.quiklinks": <Stars03 className="h-4 w-4" />,
    "dashboard.myactiveitems": <TrendUp01 className="h-4 w-4" />,
    "dashboard.taskpriorities": <CheckSquareBroken className="h-4 w-4" />,
    "dashboard.notifications": <BellRinging01 className="h-4 w-4" />,
    "dashboard.keyguidelines": <BookOpen01 className="h-4 w-4" />,
    "dashboard.upcomingmeetings": <CalendarCheck01 className="h-4 w-4" />,
    "dashboard.impacttargets": <Target04 className="h-4 w-4" />,
    "dashboard.teamcalender": <Calendar className="h-4 w-4" />,
    "dashboard.partnerdirectory": <BookClosed className="h-4 w-4" />,
    "dashboard.learninginsights": <GraduationHat02 className="h-4 w-4" />,
    "dashboard.analyticsinsights": <PieChart03 className="h-4 w-4" />,
    "dashboard.programresources": <FolderCode className="h-4 w-4" />,
    "dashboard.oversight": <Eye className="h-4 w-4" />,
    "dashboard.submissions": <File06 className="h-4 w-4" />,
    "dashboard.partnersubmissions": <File02 className="h-4 w-4" />,
    "dashboard.disbursementrequest": <CurrencyDollarCircle className="h-4 w-4" />,
    "dashboard.ideatracker": <Lightbulb05 className="h-4 w-4" />,
    "dashboard.programimpact": <BarChart10 className="h-4 w-4" />,
    "dashboard.pipelinepriority": <LineChartUp03 className="h-4 w-4"/>
  };

  const widgetSidebarConfig: Record<string, { label: string }> = {
    "dashboard.quiklinks": { label: t("components.widgets.quickAccessApps.title") },
    "dashboard.myactiveitems": { label: t("components.activePrograms.title") },
    "dashboard.taskpriorities": { label: t("components.taskPriorities.title") },
    "dashboard.notifications": { label: t("components.notificationTitle") },
    "dashboard.keyguidelines": { label: t("components.keyGuidelines.title") },
    "dashboard.upcomingmeetings": { label: t("components.MeetingsWidget.title") },
    "dashboard.impacttargets": { label: t("components.widgets.financeTargets.title") },
    "dashboard.teamcalender": { label: t("widgetSidebarConfig.TeamCalendar") },
    "dashboard.partnerdirectory": { label: t("components.partnerDirectory.title") },
    "dashboard.learninginsights": { label: t("widgetSidebarConfig.LearningInsights") },
    "dashboard.analyticsinsights": { label: t("components.analyticsInsights.financialAnalytics") },
    "dashboard.programresources": { label: t("components.widgets.programResources.title") },
    "dashboard.oversight": { label: t("components.OversightWidget.title") },
    "dashboard.submissions": { label: t("components.mySubmission.title") },
    "dashboard.partnersubmissions": { label: t("components.partnerSubmission.title") },
    "dashboard.disbursementrequest": { label: t("components.disbursementRequestsWrapper.title") },
    "dashboard.ideatracker": { label: t("components.widgets.ideasTracker.title") },
    "dashboard.programimpact": { label: t("components.widgets.programImpact.title") },
    "dashboard.pipelinepriority":{ label: t("components.PipelinePriority.title")}
  };

  const tabletHiddenWidgets = ["dashboard.quiklinks"];
  console.log("features", features);
  const widgetSidebarItems = features
    .filter((f) => f.access === true || f.access === 1)
    .filter((f) => !(isTablet && tabletHiddenWidgets.includes(f.feature_code)))
    .map((f) => ({
      key: f.feature_code,
      ...widgetSidebarConfig[f.feature_code],
    }))
    .filter((item) => item.label);

  const scrollToWidget = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;

    const navbarHeight = 72; // adjust if needed
    const y = el.getBoundingClientRect().top + window.scrollY - navbarHeight;

    window.scrollTo({
      top: y,
      behavior: "smooth",
    });
  };

  return (
    <>
      {featureStatus === "failed" && (
        <div className="fixed inset-0 flex items-center justify-center z-[999] bg-[var(--bg-surface)]">
          <div className="flex flex-col items-center gap-3 text-red-500">
            <span className="text-lg font-medium">Failed to Load Widgets</span>
            <button
              onClick={() => {
                dispatch(resetRoleState());
                if (userPrincipalName) {
                  dispatch(fetchUserDetailsByEmail(userPrincipalName));
                }
              }}
              className="px-4 py-2 text-sm bg-red-100 text-red-600 rounded"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      <header className="sticky top-0 z-[1000]">
        <div className="w-full border-b bg-[var(--nav-bg)] border-[var(--nav-border)]">
          <div className="mx-auto px-3 sm:px-4 lg:px-6">
            <div className="grid h-14 grid-cols-[auto_1fr_auto] items-center gap-3 text-[var(--text-primary)]">
              {/*  LEFT SIDE */}
              <div className="flex items-center gap-3">
                {/*  Desktop hamburger */}
               <button
  ref={menuBtnRef}
  type="button"
  aria-label={drawerOpen ? t("components.navBar.header.collapseNavigationMenu") : t("components.navBar.header.expandNavigationMenu")}
  className="hidden sm:inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[var(--sidebar-hover)]"
  onClick={() => setDrawerOpen((v) => !v)}
>
  <Menu01 className="h-4 w-4" />
</button>

                <img
                  src={MastercardLogo}
                  alt="Mastercard Foundation"
                  className="h-8 w-auto cursor-pointer"
                  onClick={() => navigate("/", { replace: true })}
                />
              </div>

              {/*  CENTER */}
              <div className="relative mx-auto min-w-0 items-center hidden sm:flex">
                {!isMobile && <TimezoneBar upn={userPrincipalName} />}
              </div>

              {/*  RIGHT SIDE */}
              <div className="flex items-center gap-2 justify-end">
                {/* ✅ Mobile hamburger */}
                <div className="sm:hidden">
                  <button
                    onClick={() => setMobileMenuOpen(true)}
                    className="h-8 w-8 flex items-center justify-center"
                  >
                    <Menu01 className="h-5 w-5" />
                  </button>
                </div>

                {/* ✅ Desktop icons */}
                <div className="hidden sm:flex items-center gap-2">
                  {showTourIcon && (
                    <>
                      <TooltipButton
                        position="bottom"
                        onClick={() => {
                          setTourOpen(true);
                          setTourTrigger((v) => v + 1);
                        }}
                        tooltip={tourOpen ? "" : t("components.navBar.tour")}
                        icon={<Star01 className="h-4 w-4" />}
                      />

                      <TourGuide userPrincipalName={userPrincipalName} flag={tourTrigger} />
                    </>
                  )}

                  {showNotificationIcon && (
                    <div className="relative" ref={notifRef}>
                      <div className="notification-icon">
                        <TooltipButton
                          tooltip={notifOpen ? "" : t("components.navBar.notifications")}
                          onClick={() => setNotifOpen((v) => !v)}
                          position="bottom"
                          icon={<Bell01 className="h-4 w-4" />}
                        />
                      </div>

                      {notifOpen && (
                        <div className="absolute right-0 mt-2 w-[360px] rounded-lg shadow-xl border z-[1000]">
                          <NotificationsDropdown />
                        </div>
                      )}
                    </div>
                  )}

                  {showLanguageSwitch && (
                    <div className="relative">
                      <TooltipButton
                        tooltip={languageOpen ? "" : t("components.navBar.language")}
                        onClick={() => {
                          setLanguageOpen((v) => !v);
                        }}
                        position="bottom"
                        icon={<Globe01 className="h-4 w-4" />}
                      />

                      <div className="absolute right-0 mt-2 z-[1100]">
                        <LanguageSwitcher
                          upn={userPrincipalName}
                          onOpenChange={setLanguageOpen}
                          hideTrigger={true}
                          forceOpen={languageOpen}
                        />
                      </div>
                    </div>
                  )}

                  <div className="profile-menu">
                    <ProfileMenu
                      displayName={displayName}
                      email={userPrincipalName}
                      onLogout={logout}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
      <aside
        ref={sidebarRef}
        className={cn(
          "hidden sm:block fixed left-0 top-14 bottom-0 overflow-y-auto border-r z-[900] transition-all duration-500",
          "bg-[var(--sidebar-bg)] border-[var(--border-color)]",
          drawerOpen ? "w-[300px]" : "w-[64px]"
        )}
      >
        <nav className="mt-2 px-2 pb-24">
          <ul className="space-y-1">
            {!featuresLoading &&
              featureStatus === "succeeded" &&
              widgetSidebarItems.map((item, index) => {
                return (
                  <li key={item.key}>
                    <button
  ref={index === 0 ? firstSidebarItemRef : null}
  onClick={() => scrollToWidget(item.key)}
  title={!drawerOpen ? item.label : ""}
  className={cn(
    "flex items-center rounded-md text-sm font-medium transition-all w-full",
    drawerOpen
      ? "gap-3 px-3 py-2"
      : "w-full h-10 flex items-center justify-center",
    "text-[var(--sidebar-text)] hover:bg-[var(--sidebar-hover)]"
  )}
>
  <div
    className={cn(
      "flex items-center justify-center w-6 h-6 shrink-0 rounded-full text-xs font-semibold",
      !widgetIconMap[item.key] && getColor(item.key)
    )}
  >
    {widgetIconMap[item.key]
      ? widgetIconMap[item.key]
      : item.label?.charAt(0).toUpperCase()}
  </div>

  {drawerOpen && <span className="truncate">{item.label}</span>}
</button>
                  </li>
                );
              })}
          </ul>
        </nav>
      </aside>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[1200]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileMenuOpen(false)} />

          <div className="fixed right-0 top-0 h-screen w-[85%] max-w-sm bg-white shadow-lg flex flex-col">
            <div className="flex items-center justify-between p-5 border-b shrink-0">
              <h2 className="text-lg font-semibold">{t("components.navBar.sidebarItems.Menu")}</h2>
              <button onClick={() => setMobileMenuOpen(false)}>✕</button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-6 text-base">
              {roles.length > 1 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                    {t("viewApplication", { defaultValue: "View Application As" })}
                  </h3>

                  <div
                    onClick={() => setRoleDropdownOpen((prev) => !prev)}
                    className="flex items-center justify-between cursor-pointer py-3 px-2 rounded-md border"
                  >
                    <span className="font-medium">
                      {ROLE_NAME_MAP[selectedRole] || selectedRole}
                    </span>

                    {roleDropdownOpen ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </div>

                  {roleDropdownOpen && (
                    <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                      {roles.map((role) => {
                        const active = role === selectedRole;

                        return (
                          <button
                            key={role}
                            onClick={() => {
                              dispatch(setSelectedRole(role));
                              setRoleDropdownOpen(false);
                              setMobileMenuOpen(false);
                            }}
                            className={`flex w-full items-center justify-between px-3 py-2 rounded-md text-sm ${active ? "bg-[var(--select-bg)]" : "hover:bg-[var(--select-bg)]"
                              }`}
                          >
                            <span className="text-[var(--text-primary)]">
                              {ROLE_NAME_MAP[role] || role}
                            </span>

                            <span
                              className="h-4 w-4 rounded-full border flex items-center justify-center"
                              style={{
                                borderColor: active ? "var(--color-brand)" : "var(--border-color)",
                              }}
                            >
                              {active && (
                                <span
                                  className="h-2 w-2 rounded-full"
                                  style={{ background: "var(--color-brand)" }}
                                />
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div>
                <div className="space-y-4">
                  <div
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/app/world-clocks");
                    }}
                    className="flex items-center gap-3 cursor-pointer"
                  >
                    <Clock className="w-5 h-5" />
                    <span>{t("components.navBar.sidebarItems.worldClocks")}</span>
                  </div>

                  <div
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/app/account-settings");
                    }}
                    className="flex items-center gap-3 cursor-pointer"
                  >
                    <Settings01 className="w-5 h-5" />
                    <span>{t("components.navBar.sidebarItems.accountSettings")}</span>
                  </div>

                  <div
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate("/app/accessibility");
                    }}
                    className="flex items-center gap-3 cursor-pointer"
                  >
                    <Sliders04 className="w-5 h-5" />
                    <span>{t("components.navBar.sidebarItems.accessibilitySettings")}</span>
                  </div>

                  <a
                    href="https://mastercardfdn.org/en/privacy/"
                    target="_blank"
                    className="flex items-center gap-3"
                  >
                    <Shield01 className="w-5 h-5" />
                    <span>{t("components.navBar.sidebarItems.privacyPolicy")}</span>
                  </a>

                  <a
                    href="https://mastercardfdn.org/en/accessibility/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <File06 className="w-5 h-5" />
                    <span>{t("components.navBar.sidebarItems.accessibility")}</span>
                  </a>

                  <a
                    href="https://cdn.buttercms.com/YefbC9GvRMCcH6pSjkzv"
                    target="_blank"
                    className="flex items-center gap-3"
                  >
                    <ShieldTick className="w-5 h-5" />
                    <span>{t("components.navBar.sidebarItems.safeguarding")}</span>
                  </a>
                </div>

                <div className="mt-6 pt-4 border-t border-[var(--border-color)]">
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      logout();
                    }}
                    className="flex w-full items-center gap-3 text-sm text-[var(--color-brand)]"
                  >
                    <XClose className="w-5 h-5" />
                    {t("signOut", { defaultValue: "Sign Out" })}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
